import { useCallback } from 'react';
import { eachUtcDayOfInterval } from '@chainflip/utils/date';
import { type UTCDate } from '@date-fns/utc';
import { ChartLegendRectangle } from '@/shared/components/ChartLegendRectangle';
import {
  type LineChartDatum,
  type LineChartMarker,
  LineChart,
} from '@/shared/components/charts/LineChart';
import { getDateAxisTicks } from '@/shared/utils/charts';
import { getLocaleDateStrings } from '@/shared/utils/date';

export const CHART_STAT_COLOR = '#46DA93';

type LendingStats = {
  day: number;
  value: number;
};
type AggregateOptions = {
  startDate: UTCDate;
  endDate: UTCDate;
};

const aggregateData = (data: LendingStats[], options: AggregateOptions) => {
  const chartDates = eachUtcDayOfInterval({
    start: options.startDate,
    end: options.endDate,
  });

  const chartData: LineChartDatum[] = chartDates.map((date) => ({
    x: date,
    y: data.find((v) => v.day === date.getTime())?.value ?? 0,
  }));

  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const averageValue = (min + max) / 2;

  return { chartData, averageValue, max };
};

type AggregatedData = ReturnType<typeof aggregateData>;

const Chart = ({ data, isLoading }: { data: AggregatedData; isLoading: boolean }) => {
  const xTickValues = getDateAxisTicks(data.chartData.map((d) => d.x));

  const markers: LineChartMarker[] = data.averageValue
    ? [{ value: data.averageValue, label: `Avg ${data.averageValue.toFixed(2)}%` }]
    : [];

  const renderTooltip = useCallback((datum: LineChartDatum) => {
    const dateOptions: Required<
      Pick<Intl.DateTimeFormatOptions, 'day' | 'month' | 'year' | 'timeZone'>
    > = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
    const labelDate = getLocaleDateStrings(datum.x as Date, dateOptions);
    const label = `${labelDate.day} ${labelDate.month} ${labelDate.year}`;

    return (
      <div className="w-[220px] space-y-3">
        <div className="flex flex-col gap-y-2.5 text-12 leading-6 text-cf-white">
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-x-1">
              <ChartLegendRectangle fill={CHART_STAT_COLOR} />
              {label}
            </div>
            {Number(datum.y).toFixed(2)}%
          </div>
        </div>
      </div>
    );
  }, []);
  return (
    <LineChart
      isLoading={isLoading}
      data={data.chartData}
      color={CHART_STAT_COLOR}
      margin={{ top: data.max === data.averageValue ? 27 : 20, right: 40, bottom: 50, left: 50 }}
      xTickValues={xTickValues}
      yTickFormat={(value) => `${value}%`}
      markers={markers}
      renderTooltip={renderTooltip}
    />
  );
};

export const marketApyChartProps = {
  Chart,
  aggregateData,
};
